/**
 * Fires a GA4 event via gtag.
 * Fails silently if GA4 is not loaded (e.g., ad blocker active or no measurement ID).
 *
 * Reserved param names — DO NOT use as custom event params:
 *   `source`, `medium`, `campaign`, `term`, `content`, `page_referrer`.
 * GA4 treats these as attribution dimensions and will repopulate the session's
 * source/medium from them, sending the session to the Unassigned channel.
 * Use `trigger`, `placement`, `surface`, `location` instead.
 */
export function trackEvent(eventName: string, params?: Record<string, string | boolean>) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
}

export type AuthMethod = 'google' | 'microsoft' | 'apple' | 'email';

/**
 * GA4 recommended `sign_up` event. Fire on the SSO call that first creates an
 * account for this browser. `method` is the provider name.
 */
export function trackSignUp(
  method: AuthMethod,
  extra?: Record<string, string | boolean>,
) {
  trackEvent('sign_up', { method, ...(extra ?? {}) });
}

/**
 * GA4 recommended `login` event. Fire on returning-user SSO success.
 */
export function trackLogin(method: AuthMethod) {
  trackEvent('login', { method });
}

// ── Email auth funnel ──────────────────────────────────────────────────────
// Lets us trace where users drop in the new email flow:
//   email_request_sent → email_code_entered → sign_up { method: 'email' }
// and separately, whether the Google button ever became visible
// (email_button_blocked fires when GSI fails to render within 3s — the
// signal that confirms the original conversion blocker).

export function trackEmailRequestSent() {
  trackEvent('email_request_sent');
}

export function trackEmailCodeEntered() {
  trackEvent('email_code_entered');
}

export function trackEmailLinkClicked() {
  trackEvent('email_link_clicked');
}

export function trackEmailVerifyFailed(reason: 'invalid' | 'expired' | 'rate_limited' | 'network') {
  trackEvent('email_verify_failed', { reason });
}

export function trackGoogleButtonBlocked() {
  trackEvent('email_button_blocked');
}

// ── Body shape calculator (Style Hub embed) ────────────────────────────────
// Privacy note: we only ever send the derived shape — never the raw
// measurements. Body measurements are sensitive data and must not reach GA4.

/**
 * Fires when the embedded calculator produces a result.
 * `shape` is the snake_case shape name (e.g. 'inverted_triangle'),
 * `unit` is what the reader measured in, `borderline` whether we surfaced a
 * neighbouring shape.
 */
export function trackBodyShapeCalculated(
  shape: string,
  unit: 'in' | 'cm',
  borderline: boolean,
) {
  trackEvent('body_shape_calculated', { shape, unit, borderline });
}

/**
 * Fires when a reader clicks one of the result links (dresses guide, capsule
 * guide, shop edit, or the colour quiz CTA).
 */
export function trackBodyShapeCalculatorCta(
  shape: string,
  destination: 'dresses_guide' | 'capsule_guide' | 'shop' | 'colour_quiz',
) {
  trackEvent('body_shape_calculator_cta', { shape, destination });
}

// ── Style Hub quiz CTA ─────────────────────────────────────────────────────
// The in-article card inviting body shape and colour readers into the matching
// public quiz. `article_slug` rather than `content`, which GA4 reserves.

export function trackQuizCtaView(
  quiz: 'body-shape' | 'colour-analysis',
  articleSlug: string,
  placement: 'top' | 'mid' | 'end',
) {
  trackEvent('quiz_cta_view', { quiz, article_slug: articleSlug, placement });
}

/**
 * A Style Hub guide's "shop pieces for your shape / palette" link. `filter` is
 * the shop param it opened with, `value` the shape or palette name, and
 * `placement` which of the two links it was: the one under the article's
 * title or the card after the body.
 */
export function trackArticleShopClick(
  articleSlug: string,
  filter: 'body_type' | 'colour_palette',
  value: string,
  placement: 'top' | 'end',
) {
  trackEvent('article_shop_click', { article_slug: articleSlug, filter, value, placement });
}

/**
 * `answer` is the first question's option id when the reader answered it in
 * the article (the top block), and absent for a plain "take the quiz" link.
 */
export function trackQuizCtaClick(
  quiz: 'body-shape' | 'colour-analysis',
  articleSlug: string,
  placement: 'top' | 'mid' | 'end',
  answer?: string,
) {
  trackEvent('quiz_cta_click', {
    quiz,
    article_slug: articleSlug,
    placement,
    ...(answer ? { answer } : {}),
  });
}

/** An App Store or Google Play badge outside the app banner. */
export function trackAppBadgeClick(
  store: 'app_store' | 'google_play',
  placement: 'quiz_result' | 'my_hub',
  platform: 'ios' | 'android' | 'other',
) {
  trackEvent('app_badge_click', { store, placement, platform });
}
